import { ForwardedRef, forwardRef } from 'react';
import {
  Group,
  TextArea as AriaTextArea,
  TextField,
  type TextAreaProps,
  type TextFieldProps,
} from 'react-aria-components';

import { Field, type FieldProps } from '../field';
import styles from './Input.module.css';

export type Props = Pick<
  TextFieldProps,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'onBlur'
  | 'autoFocus'
  | 'isDisabled'
  | 'isReadOnly'
  | 'maxLength'
  | 'name'
  | 'slot'
> &
  Pick<TextAreaProps, 'placeholder' | 'rows'> &
  FieldProps & {
    maxWidth?: string | number;
  };

/**
 * `Input` for prose: the same label, description and error frame, and the same bordered box,
 * around a control that wraps and that the reader can drag taller.
 *
 * It shares `Input.module.css` rather than owning a stylesheet, so the two controls cannot
 * drift apart on border colour, focus ring or invalid state — that file's selectors already
 * name `textarea` beside `input`. What differs is the box: a single-line group centres its
 * content against a 32px minimum, which would leave a multi-line control floating inside its
 * own border, so `.groupMultiline` stretches it to fill and gives it a vertical resize handle.
 */
export const TextArea = forwardRef(function _TextArea(
  {
    label,
    isLabelVisuallyHidden,
    description,
    error,
    isRequired,
    maxWidth,
    isDisabled,
    placeholder,
    rows,
    ...textFieldProps
  }: Props,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  const isInvalid = Boolean(error);
  return (
    <TextField {...textFieldProps} className={styles.field} isDisabled={isDisabled} isInvalid={isInvalid}>
      <Field
        label={label}
        isLabelVisuallyHidden={isLabelVisuallyHidden}
        description={description}
        error={error}
        isRequired={isRequired}
      >
        <Group
          className={`${styles.group} ${styles.groupMultiline}`}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          style={{ maxWidth }}
        >
          <AriaTextArea placeholder={placeholder} rows={rows} ref={ref} />
        </Group>
      </Field>
    </TextField>
  );
});
